import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { FadeUp, Overline, Stagger, StaggerItem, MaskedLine } from "@/components/site/Reveal";
import { api, postContact } from "@/lib/api";
import { YOUTUBE_URL, IMAGES } from "@/data/content";

const inputCls =
  "border border-white/20 bg-black/40 px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-brand-accent focus:ring-1 focus:ring-brand-accent";

/* ---------------- Section 2 — Be Part of FORE ---------------- */
const OPPORTUNITIES = [
  {
    n: "01",
    title: "Golf Outings",
    body: "Community rounds, scrambles, golf outings, and opportunities to get on the course with FORE. All skill levels are welcome. The golf doesn't have to be great—the experience should be.",
    cta: "Bring FORE to Your Outing",
    href: "#host-event",
  },
  {
    n: "02",
    title: "Charity & Community Events",
    body: "Golf gives people a reason to come together, and we want to use that platform to support causes, organizations, and communities that matter.",
    cta: "Tell Us About Your Event",
    href: "#host-event",
  },
  {
    n: "03",
    title: "Brand & Course Partnerships",
    body: "FORE works with golf courses, businesses, brands, and organizations to create experiences that connect people through golf and content.",
    cta: "Partner With FORE",
    href: "/sponsor",
  },
];

/* ---------------- Section 3 — The FORE Community ---------------- */
const EXPERIENCES = [
  {
    title: "Guest Rounds",
    body: "Interesting people. Eighteen holes. Conversations you probably wouldn't get anywhere else.",
    image: IMAGES.walking,
    cta: "Watch FORE",
    href: "/videos",
  },
  {
    title: "Challenges",
    body: "Competitive, ridiculous, and sometimes questionable golf.",
    image: IMAGES.hero,
    cta: "Watch FORE",
    href: "/videos",
  },
];

const BePartSection = () => (
  <section id="be-part" className="scroll-mt-24 border-t border-white/10 bg-ink-2" data-testid="be-part">
    <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
      <FadeUp className="max-w-3xl">
        <Overline>Be Part of FORE</Overline>
        <h2 className="mt-4 font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tighter sm:text-5xl" data-testid="be-part-heading">
          There's more than one way<br />to <span className="text-brand-accent">tee it up</span> with us.
        </h2>
      </FadeUp>
      <Stagger className="mt-14 grid gap-x-12 gap-y-14 lg:grid-cols-3">
        {OPPORTUNITIES.map((o) => (
          <StaggerItem key={o.n}>
            <div className="flex h-full flex-col border-t-2 border-brand-accent/70 pt-6" data-testid={`opportunity-${o.n}`}>
              <span className="font-mono text-xs tracking-[0.35em] text-brand-accent/70">{o.n}</span>
              <h3 className="mt-4 font-display text-2xl font-extrabold uppercase tracking-tight text-white md:text-3xl">
                {o.title}
              </h3>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-muted md:text-base">{o.body}</p>
              {o.href.startsWith("#") ? (
                <a
                  href={o.href}
                  data-testid={`opportunity-cta-${o.n}`}
                  className="group mt-8 inline-flex w-fit items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-white/70 transition-colors duration-300 hover:text-brand-accent"
                >
                  {o.cta}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              ) : (
                <Link
                  to={o.href}
                  data-testid={`opportunity-cta-${o.n}`}
                  className="group mt-8 inline-flex w-fit items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-white/70 transition-colors duration-300 hover:text-brand-accent"
                >
                  {o.cta}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              )}
            </div>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const ExperiencesSection = () => (
  <section className="border-t border-white/10" data-testid="community-experiences">
    <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
      <FadeUp className="max-w-3xl">
        <Overline>The FORE Community</Overline>
        <h2 className="mt-4 font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tighter sm:text-5xl" data-testid="experiences-heading">
          Come for the golf.<br />Stay for <span className="text-brand-accent">everything around it.</span>
        </h2>
      </FadeUp>
      <div className="mt-16 flex flex-col gap-16 md:mt-20 md:gap-24">
        {EXPERIENCES.map((e, i) => {
          const flip = i % 2 === 1;
          return (
            <FadeUp key={e.title}>
              <div className="grid items-center gap-8 lg:grid-cols-12 lg:gap-14" data-testid={`experience-${e.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className={`overflow-hidden lg:col-span-6 ${flip ? "lg:order-2" : ""}`}>
                  <img
                    src={e.image}
                    alt={e.title}
                    className="aspect-[16/10] w-full object-cover contrast-110 saturate-[0.9] transition-transform duration-700 ease-out hover:scale-[1.02]"
                  />
                </div>
                <div className={`lg:col-span-6 ${flip ? "lg:order-1" : ""}`}>
                  <h3 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white md:text-4xl">{e.title}</h3>
                  <p className="mt-4 max-w-lg text-base leading-relaxed text-muted">{e.body}</p>
                  {e.href.startsWith("#") ? (
                    <a href={e.href} data-testid={`experience-cta-${e.title.toLowerCase().replace(/\s+/g, "-")}`} className="group mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-white/70 transition-colors duration-300 hover:text-brand-accent">
                      {e.cta}
                      <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                    </a>
                  ) : e.href.startsWith("http") ? (
                    <a href={e.href} target="_blank" rel="noopener noreferrer" data-testid={`experience-cta-${e.title.toLowerCase().replace(/\s+/g, "-")}`} className="group mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-white/70 transition-colors duration-300 hover:text-brand-accent">
                      {e.cta}
                      <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                  ) : (
                    <Link to={e.href} data-testid={`experience-cta-${e.title.toLowerCase().replace(/\s+/g, "-")}`} className="group mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-white/70 transition-colors duration-300 hover:text-brand-accent">
                      {e.cta}
                      <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  )}
                </div>
              </div>
            </FadeUp>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---------------- Section 4 — Community Spotlight (hidden until entries exist) ------- */
const SpotlightSection = () => {
  const [entries, setEntries] = useState(null);

  useEffect(() => {
    api.get("/community/spotlight").then((r) => setEntries(r.data.entries || [])).catch(() => setEntries([]));
  }, []);

  if (!entries || entries.length === 0) return null;

  return (
    <section className="border-t border-white/10 bg-ink-2" data-testid="community-spotlight">
      <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
        <FadeUp className="max-w-3xl">
          <Overline>Community Spotlight</Overline>
          <h2 className="mt-4 font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tighter sm:text-5xl" data-testid="spotlight-heading">
            From the <span className="text-brand-accent">FORE community.</span>
          </h2>
        </FadeUp>
        <Stagger className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {entries.map((e) => (
            <StaggerItem key={e.id}>
              <article className="group flex h-full flex-col overflow-hidden border border-white/10 bg-ink" data-testid={`spotlight-${e.id}`}>
                {e.photo && (
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={e.photo} alt={e.name} className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]" />
                  </div>
                )}
                <div className="flex flex-1 flex-col p-7">
                  {e.category && (
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand-accent">{e.category}</p>
                  )}
                  <h3 className="mt-3 font-display text-2xl font-extrabold uppercase tracking-tight text-white">{e.name}</h3>
                  {e.description && <p className="mt-3 flex-1 text-sm leading-relaxed text-muted">{e.description}</p>}
                  {e.link && (
                    <a
                      href={e.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`spotlight-link-${e.id}`}
                      className="group/link mt-6 inline-flex w-fit items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-white/70 transition-colors duration-300 hover:text-brand-accent"
                    >
                      Learn more
                      <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
                    </a>
                  )}
                </div>
              </article>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
};

/* ---------------- Section 5 — Want FORE at your event? ---------------- */
const EVENT_TYPES = ["Charity Tournament", "Company Outing", "Fundraiser", "Community Round", "Golf Event", "Other"];

const EventSection = () => {
  const [form, setForm] = useState({
    event_name: "",
    name: "",
    email: "",
    phone: "",
    event_date: "",
    event_location: "",
    event_type: EVENT_TYPES[0],
    expected_attendance: "",
    involvement: "",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await postContact({
        name: form.name,
        email: form.email,
        phone: form.phone || undefined,
        company: form.event_name,
        inquiry_type: "event",
        event_date: form.event_date || undefined,
        event_location: form.event_location || undefined,
        event_type: form.event_type,
        expected_attendance: form.expected_attendance || undefined,
        involvement: form.involvement,
        message: form.message,
      });
      setDone(true);
      toast.success("Event submitted — we'll be in touch.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="host-event" className="scroll-mt-24 border-t border-white/10" data-testid="host-event">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-20 md:px-8 md:py-28 lg:grid-cols-2 lg:gap-20">
        <FadeUp>
          <Overline>Want FORE At Your Event?</Overline>
          <h2 className="mt-4 font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tighter sm:text-5xl" data-testid="host-event-heading">
            Hosting an outing?<br />Let's make it <span className="text-brand-accent">more memorable.</span>
          </h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-muted">
            Whether you're organizing a charity tournament, company outing, fundraiser, or golf
            event, FORE is always looking for great people and great reasons to get on the course.
            Tell us what you're putting together and how you'd like us involved.
          </p>
        </FadeUp>

        <FadeUp delay={0.1}>
          {done ? (
            <div className="grid h-full min-h-[300px] place-items-center border border-white/10 bg-ink-2 p-10 text-center" data-testid="event-success">
              <div>
                <p className="font-display text-2xl font-extrabold uppercase tracking-tight text-brand-accent">Got it.</p>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-muted">
                  Your event details just landed in our inbox. We'll look it over and get back to you.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="grid gap-4" data-testid="event-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <input required value={form.event_name} onChange={set("event_name")} placeholder="Event / organization name" data-testid="event-field-event-name" className={inputCls} />
                <input required value={form.name} onChange={set("name")} placeholder="Contact name" data-testid="event-field-name" className={inputCls} />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <input required type="email" value={form.email} onChange={set("email")} placeholder="Email" data-testid="event-field-email" className={inputCls} />
                <input type="tel" value={form.phone} onChange={set("phone")} placeholder="Phone (optional)" data-testid="event-field-phone" className={inputCls} />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <input required type="date" value={form.event_date} onChange={set("event_date")} data-testid="event-field-date" aria-label="Event date" className={`${inputCls} [color-scheme:dark]`} />
                <input required value={form.event_location} onChange={set("event_location")} placeholder="Golf course / location" data-testid="event-field-location" className={inputCls} />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <select value={form.event_type} onChange={set("event_type")} data-testid="event-field-type" aria-label="Type of event" className={`${inputCls} appearance-none`}>
                  {EVENT_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <input required value={form.expected_attendance} onChange={set("expected_attendance")} placeholder="Expected attendance / golfers" data-testid="event-field-attendance" className={inputCls} />
              </div>
              <textarea required rows={3} value={form.involvement} onChange={set("involvement")} placeholder="How would you like FORE involved?" data-testid="event-field-involvement" className={`${inputCls} resize-none`} />
              <textarea required rows={3} value={form.message} onChange={set("message")} placeholder="Additional details" data-testid="event-field-details" className={`${inputCls} resize-none`} />
              <button
                type="submit"
                disabled={busy}
                data-testid="event-submit"
                className="group flex items-center justify-center gap-3 bg-white px-7 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-brand-accent disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Your Event"}
              </button>
            </form>
          )}
        </FadeUp>
      </div>
    </section>
  );
};

/* ---------------- Section 6 — Final CTA ---------------- */
const FinalCta = () => (
  <section className="border-t border-white/10 bg-ink-2" data-testid="community-final">
    <div className="mx-auto max-w-7xl px-5 py-24 text-center md:px-8 md:py-32">
      <FadeUp>
        <h2 className="font-display text-5xl font-black uppercase leading-[0.9] tracking-tighter sm:text-6xl lg:text-7xl" data-testid="community-final-heading">
          See you on<br />the <span className="text-brand-accent">course.</span>
        </h2>
        <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-muted">
          Follow FORE, watch the latest rounds, or get involved with what we're building next.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <a
            href={YOUTUBE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="final-cta-watch"
            className="group flex items-center gap-3 bg-white px-7 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-brand-accent"
          >
            Watch FORE
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href={YOUTUBE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="final-cta-follow"
            className="group flex items-center gap-3 border border-white/30 px-7 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-brand-accent hover:text-brand-accent"
          >
            Follow the Journey
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <Link
            to="/contact"
            data-testid="final-cta-contact"
            className="group flex items-center gap-3 border border-white/30 px-7 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-brand-accent hover:text-brand-accent"
          >
            Contact Us
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </FadeUp>
    </div>
  </section>
);

export default function Community() {
  return (
    <main data-testid="community-page">
      {/* SECTION 1 — HERO */}
      <section className="mx-auto max-w-7xl px-5 pb-16 pt-36 md:px-8 md:pb-24 md:pt-48" data-testid="community-hero">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <MaskedLine>
              <Overline>Community</Overline>
            </MaskedLine>
            <h1 className="mt-8 font-display text-5xl font-black uppercase leading-[0.9] tracking-tighter sm:text-6xl lg:text-7xl" data-testid="community-heading">
              <MaskedLine delay={0.15}>Golf is better</MaskedLine>
              <MaskedLine delay={0.3}>with <span className="text-brand-accent">people.</span></MaskedLine>
            </h1>
            <FadeUp delay={0.5}>
              <p className="mt-8 max-w-xl text-base leading-relaxed text-muted md:text-lg">
                FORE was built around the people you meet, the stories you hear, and the memories
                that happen over 18 holes. From community rounds and charity outings to
                partnerships and events, we want to give people more reasons to get out, tee it
                up, and be part of something.
              </p>
            </FadeUp>
            <FadeUp delay={0.65}>
              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  href="#be-part"
                  data-testid="community-cta-involved"
                  className="group flex items-center gap-3 bg-white px-7 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-brand-accent"
                >
                  Get Involved
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </a>
                <Link
                  to="/sponsor"
                  data-testid="community-cta-work"
                  className="group flex items-center gap-3 border border-white/30 px-7 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-brand-accent hover:text-brand-accent"
                >
                  Work With FORE
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </FadeUp>
          </div>
          <FadeUp delay={0.3}>
            <img
              src="/founders.jpg"
              alt="Carlos and Joe of FORE the Hell of It on the course"
              className="w-full object-cover"
              data-testid="community-hero-image"
            />
          </FadeUp>
        </div>
      </section>

      <BePartSection />
      <ExperiencesSection />
      <EventSection />
      <SpotlightSection />
      <FinalCta />
    </main>
  );
}
